import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from '../ui/PageShell';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import Footer from '../Footer';

const VerifyEmailPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const purpose = params.get('purpose') || undefined;
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [resendMsg, setResendMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('This confirmation link is missing or incomplete.');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    void apiService
      .verifyEmail(token, purpose)
      .then((res: any) => {
        if (cancelled) return;
        if (res.success) {
          setStatus('success');
          setMessage(res.message || 'Your email is confirmed.');
        } else {
          setStatus('error');
          setMessage(res.error || 'Unable to confirm this email.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(err?.message || 'Unable to confirm this email.');
      });
    return () => {
      cancelled = true;
    };
  }, [token, purpose]);

  return (
    <PageShell>
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-400/90 mb-2">Account</p>
        <h1 className="font-display text-3xl text-white font-semibold">Confirm your email</h1>
        {status === 'loading' ? <p className="mt-4 text-gray-400">Confirming…</p> : null}
        {status !== 'loading' ? (
          <p className={`mt-4 text-sm ${status === 'success' ? 'text-emerald-300' : 'text-red-300'}`} role="status">
            {message}
          </p>
        ) : null}
        <div className="mt-8 flex flex-col sm:flex-row gap-2 justify-center">
          <Link to="/" className="btn-primary min-h-[44px] inline-flex items-center justify-center px-4 rounded-lg">
            Continue
          </Link>
          {isAuthenticated ? (
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => {
                setResendMsg('');
                void apiService.resendVerification().then((res: any) => {
                  setResendMsg(res.message || 'If needed, we sent another email.');
                });
              }}
            >
              Resend confirmation
            </Button>
          ) : null}
        </div>
        {resendMsg ? <p className="mt-3 text-sm text-gray-400">{resendMsg}</p> : null}
      </div>
      <Footer />
    </PageShell>
  );
};

export default VerifyEmailPage;
